import { makeAutoObservable } from "mobx";
import {
  AuditorListItem,
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
} from "@/dbot/common/commonDomain";

import { emailValidator } from "@/common/common";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { StringFieldModel } from "@/common/commonForms";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { debounce } from "lodash";

export type ChangeOfAuditor = {
  type: DecisionType.auditor;
  name?: string;
  email?: string;
  organisationNumber?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

export class ChangeOfAuditorForm {
  readonly type = DecisionType.auditor;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext<ChangeOfAuditor>;
  readonly data?: ChangeOfAuditor;

  searchCompanyModel: StringFieldModel;
  searchCompanies: (input: string) => void;
  suggestedCompanyForAuditors: SuggestedCompany[] = [];
  selectedCompanyForAuditor?: SuggestedCompany;
  emailModel: StringFieldModel;
  currentAuditor?: AuditorListItem;
  validationError?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<ChangeOfAuditor>,
    data?: ChangeOfAuditor,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });

    this.dispatch = dispatch;
    this.context = context;
    this.data = data;
    this.currentAuditor = context.contextData[context.orgNo]?.initialState?.currentAuditor;
    this.selectedCompanyForAuditor = mapChangeOfCompanyAuditorToListItem(data);
    this.searchCompanyModel = new StringFieldModel({
      value: this.selectedCompanyForAuditor?.name ?? "",
      required: true,
    });
    this.searchCompanies = debounce((input: string) => this.findCompanies(input), 250);
    this.emailModel = new StringFieldModel({
      required: true,
      value: data?.email ?? "",
      validator: emailValidator,
    });
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.suggestedCompanyForAuditors = event.result;
        }
        break;
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return this.emailModel.isValid && this.selectedCompanyForAuditor !== undefined;
  }

  validate(): boolean {
    this.emailModel.validate();
    this.validationError =
      this.selectedCompanyForAuditor !== undefined ? undefined : { key: "required" };
    return this.isValid;
  }

  setSelectedCompanyForAuditor(company: SuggestedCompany) {
    this.searchCompanyModel.setAndCommitInputValue(company.name);
    this.selectedCompanyForAuditor = company;
    this.validationError = undefined;
  }

  clearSelectedAuditor() {
    this.searchCompanyModel.clear();
    this.selectedCompanyForAuditor = undefined;
    this.validationError = undefined;
  }

  setCompanySearch(input: string) {
    this.searchCompanyModel.setInputValue(input);
    this.searchCompanies(input);
  }

  findCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }
}

export function mapChangeOfCompanyAuditorToListItem(
  data?: ChangeOfAuditor,
): SuggestedCompany | undefined {
  return data && data.name && data.organisationNumber
    ? {
        name: data.name,
        organizationnumber: data.organisationNumber,
        address: data.businessAddress1,
        address2: data.businessAddress2,
        zip: data.businessZip,
        city: data.businessCity,
        sourceCountry: data.businessCountry,
      }
    : undefined;
}
